import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SignatureUpload } from '@/components/settings/reporting/SignatureUpload';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { useGroupLogo } from '@/hooks/use-group-logo';
import { ApiError } from '@/lib/api/client';
import { useTranslation } from 'react-i18next';
/** Server-enforced ceiling (PUT answers 400 above it); mirrored client-side so
 *  an oversized pick fails instantly instead of after the upload. */
const MAX_LOGO_KB = 512;
/**
 * Branding — the group's logo.
 *
 * It is not decoration: this is the mark a referring practice sees above the
 * fold on the anonymous enrollment landing page, where borrowed trust from a
 * radiologist they already work with is what converts (spec §10.2). A group
 * with no logo falls back to an initials tile there.
 */
export function BrandingTab({ groupId }) {
    const { t } = useTranslation('settings');
    const { logo, upload, remove } = useGroupLogo(groupId);
    if (logo.isLoading) {
        return _jsx(Skeleton, { className: "h-40 w-full", "data-testid": "branding-loading" });
    }
    const intro = (_jsx("p", { className: "text-muted-foreground max-w-xl text-sm", children: t('settings.tcGroups.branding.intro') }));
    // The read is group-admin-gated server-side, and this tab is reachable by
    // anyone the coarse settings-category gate lets through (admin of ANY group,
    // or a clinic admin). Say so, rather than rendering the empty upload
    // placeholder, which would read as "no logo set" when it means "not yours".
    if (logo.isError) {
        const forbidden = logo.error instanceof ApiError && logo.error.status === 403;
        return (_jsxs("div", { className: "space-y-4", "data-testid": "branding-tab", children: [intro, _jsx("p", { className: "text-destructive text-sm", "data-testid": "branding-error", children: forbidden
                        ? t('settings.tcGroups.branding.errorForbidden')
                        : t('settings.tcGroups.branding.errorGeneric') })] }));
    }
    return (_jsxs("div", { className: "space-y-6", "data-testid": "branding-tab", children: [intro, _jsxs("div", { className: "max-w-xs space-y-2", children: [_jsx(Label, { className: "text-muted-foreground text-xs", children: t('settings.tcGroups.branding.logoLabel') }), _jsx(SignatureUpload, { currentUrl: logo.data?.logoUrl ?? null, onFilePicked: (file) => upload.mutate(file), onRemove: () => remove.mutate(), removeConfirmTitle: t('settings.tcGroups.branding.removeTitle'), removeConfirmBody: t('settings.tcGroups.branding.removeConfirm'), placeholderIcon: "image", placeholderText: t('settings.tcGroups.branding.uploadCta'), maxSizeKb: MAX_LOGO_KB, disabled: upload.isPending || remove.isPending }), _jsx("p", { className: "text-muted-foreground/70 text-[11px] leading-relaxed", children: t('settings.tcGroups.branding.placementHint') })] })] }));
}
